import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { EMPTY, interval } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'

import { ChatService } from '@services/chat-service'
import { ChatState } from '@states/chat-state'
import { OperatorDataState } from '@states/operator-data'
import { ChatConversationList } from '@components/chat-conversation-list'
import { ChatMessageView } from '@components/chat-message-view'

const CONVERSATIONS_POLL_MS = 30_000
const MESSAGES_POLL_MS = 15_000

@Component({
	selector: 'chat-overlay',
	template: `
		<div class="overlay-header">
			<span class="overlay-title">Chat</span>
			<button type="button" mat-icon-button class="close-btn" (click)="close()">
				<mat-icon>close</mat-icon>
			</button>
		</div>
		<div class="overlay-body">
			<chat-conversation-list
				(select)="selectConversation($event)"
				(refresh)="refreshConversations()"
			/>
			<chat-message-view (refresh)="refreshMessages()" />
		</div>
	`,
	styleUrl: './chat-overlay.scss',
	standalone: true,
	imports: [MatButtonModule, MatIconModule, ChatConversationList, ChatMessageView],
})
export class ChatOverlay implements OnInit, OnDestroy {
	readonly #ref = inject<MatDialogRef<ChatOverlay>>(MatDialogRef)
	readonly #document = inject(DOCUMENT)
	readonly #chatService = inject(ChatService)
	readonly #chatState = inject(ChatState)
	readonly #operatorDataState = inject(OperatorDataState)
	readonly #destroyRef = inject(DestroyRef)

	constructor() {
		this.#startPolling()
	}

	ngOnInit(): void {
		this.#document.body.classList.add('chat-open')
		this.#loadDevices()
		this.#loadConversations()
	}

	ngOnDestroy(): void {
		this.#document.body.classList.remove('chat-open')
	}

	close(): void {
		this.#ref.close()
	}

	selectConversation(uuid: string): void {
		this.#chatState.activeConversationUuid.set(uuid)
		this.#loadMessages(uuid)
		this.#markAsRead(uuid)
	}

	refreshConversations(): void {
		this.#loadConversations()
	}

	refreshMessages(): void {
		const uuid = this.#chatState.activeConversationUuid()
		if (!uuid) return
		this.#loadMessages(uuid)
	}

	#startPolling(): void {
		const requestPointUuid = () => this.#operatorDataState.metadata()?.requestPointUuid ?? ''

		interval(CONVERSATIONS_POLL_MS).pipe(
			switchMap(() => this.#chatService.getConversations(requestPointUuid())),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(conversations => this.#chatState.conversations.set(conversations))

		toObservable(this.#chatState.activeConversationUuid).pipe(
			switchMap(uuid => uuid
				? interval(MESSAGES_POLL_MS).pipe(switchMap(() => this.#chatService.getMessages(uuid)))
				: EMPTY
			),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(messages => this.#chatState.messages.set(messages))
	}

	#loadDevices(): void {
		this.#chatService.getDevices().pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(devices => this.#chatState.devices.set(devices.filter(d => d.displayName !== null)))
	}

	#loadConversations(): void {
		const requestPointUuid = this.#operatorDataState.metadata()?.requestPointUuid ?? ''

		this.#chatState.isLoadingConversations.set(true)
		this.#chatService.getConversations(requestPointUuid).pipe(
			finalize(() => this.#chatState.isLoadingConversations.set(false)),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(conversations => this.#chatState.conversations.set(conversations))
	}

	#loadMessages(conversationUuid: string): void {
		this.#chatState.isLoadingMessages.set(true)
		this.#chatService.getMessages(conversationUuid).pipe(
			finalize(() => this.#chatState.isLoadingMessages.set(false)),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(messages => this.#chatState.messages.set(messages))
	}

	#markAsRead(conversationUuid: string): void {
		const requestPointUuid = this.#operatorDataState.metadata()?.requestPointUuid ?? ''

		this.#chatService.markAsRead(conversationUuid, requestPointUuid).pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe()
	}
}
